import React, { useEffect, useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  TextField,
  Button,
  Box,
  Typography,
  Grid,
  Paper,
  MenuItem,
  CircularProgress,
  IconButton,
  Stack,
} from "@mui/material";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import PhotoLibrary from "@mui/icons-material/PhotoLibrary";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import { useNavigate, useParams } from "react-router-dom";
import { ExpenseApi, ProjectApi, UserApi } from "../../apis";
import { toast } from "react-toastify";
import {
  EXPENSE_CATEGORIES,
  getExpenseBasePath,
  loadSelectableProjects,
} from "./helpers";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdfOutlined";
import { isPdfReceipt } from "../../utils/receipts";

const validationSchema = Yup.object({
  projectId: Yup.string().required("Project is required"),
  phaseId: Yup.string(),
  category: Yup.string()
    .oneOf(EXPENSE_CATEGORIES, "Select a valid category")
    .required("Category is required"),
  amount: Yup.number()
    .typeError("Amount must be a number")
    .positive("Amount must be greater than zero")
    .required("Amount is required"),
  date: Yup.string().required("Date is required"),
  notes: Yup.string(),
});

const todayIso = () => new Date().toISOString().slice(0, 10);

function AddExpense() {
  const navigate = useNavigate();
  const { expenseId } = useParams();
  const isEditing = Boolean(expenseId);
  const basePath = getExpenseBasePath();

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialising, setInitialising] = useState(true);
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [existingReceiptUrl, setExistingReceiptUrl] = useState(null);

  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const formik = useFormik({
    initialValues: {
      projectId: "",
      phaseId: "",
      category: "",
      amount: "",
      date: todayIso(),
      notes: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const payload = { ...values };
        if (receiptFile) {
          payload.receipt = receiptFile;
        }

        if (isEditing) {
          await ExpenseApi.updateExpense(expenseId, payload);
        } else {
          await ExpenseApi.createExpense(payload);
        }

        navigate(`${basePath}/expenses`);
      } catch (error) {
        console.error("Error saving expense:", error);
        toast.error(error.response?.data?.message || "Failed to save expense");
      } finally {
        setLoading(false);
      }
    },
  });

  const { setValues } = formik;

  useEffect(() => {
    const load = async () => {
      try {
        setProjects(await loadSelectableProjects({ ProjectApi, UserApi }));

        if (isEditing) {
          const res = await ExpenseApi.getExpenseById(expenseId);
          const expense = res?.data?.data?.expense;
          if (expense) {
            setValues({
              projectId: String(expense.projectId ?? ""),
              phaseId: expense.phaseId ? String(expense.phaseId) : "",
              category: expense.category ?? "",
              amount: expense.amount ?? "",
              date: expense.date ? String(expense.date).slice(0, 10) : todayIso(),
              notes: expense.notes ?? "",
            });
            setExistingReceiptUrl(expense.receiptUrl || null);
          }
        }
      } catch (error) {
        console.error("Error loading expense form:", error);
        toast.error("Failed to load expense form");
      } finally {
        setInitialising(false);
      }
    };

    load();
  }, [expenseId, isEditing, setValues]);

  // Object URLs for the local preview must be released when the choice changes.
  useEffect(() => {
    if (!receiptFile) {
      setReceiptPreview(null);
      return undefined;
    }

    const objectUrl = URL.createObjectURL(receiptFile);
    setReceiptPreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [receiptFile]);

  const phaseOptions = useMemo(() => {
    const selected = projects.find(
      (project) => String(project.id) === String(formik.values.projectId)
    );
    return selected?.phases || [];
  }, [projects, formik.values.projectId]);

  const handleProjectChange = (event) => {
    formik.setFieldValue("projectId", event.target.value);
    formik.setFieldValue("phaseId", "");
  };

  const handleReceiptChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setReceiptFile(file);
    }
    event.target.value = "";
  };

  const clearReceipt = () => {
    setReceiptFile(null);
    setExistingReceiptUrl(null);
  };

  const previewSrc = receiptPreview || existingReceiptUrl;
  // A PDF has no thumbnail to show, so the preview becomes a named link to
  // open it rather than an <img> that would render as a broken image.
  const previewIsPdf = isPdfReceipt(receiptFile || existingReceiptUrl);

  if (initialising) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", minHeight: 300, alignItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight={600} gutterBottom mb={{ xs: 2, sm: 4 }}>
        {isEditing ? "Edit Expense" : "Add Expense"}
      </Typography>

      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mx: { xs: -2, md: 0 }, borderRadius: { xs: 0, md: 1 } }}>
        <Box component="form" onSubmit={formik.handleSubmit}>
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Typography variant="subtitle2" component="div" gutterBottom fontWeight={600}>
                Project
              </Typography>
              <TextField
                select
                fullWidth
                id="projectId"
                name="projectId"
                value={formik.values.projectId}
                onChange={handleProjectChange}
                onBlur={formik.handleBlur}
                error={formik.touched.projectId && Boolean(formik.errors.projectId)}
                helperText={formik.touched.projectId && formik.errors.projectId}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">
                  <em>Select a project</em>
                </MenuItem>
                {projects.map((project) => (
                  <MenuItem key={project.id} value={String(project.id)}>
                    {project.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2.5}>
              <Typography variant="subtitle2" component="div" gutterBottom fontWeight={600}>
                Phase
              </Typography>
              <TextField
                select
                fullWidth
                id="phaseId"
                name="phaseId"
                value={formik.values.phaseId}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={!formik.values.projectId}
                error={formik.touched.phaseId && Boolean(formik.errors.phaseId)}
                helperText={
                  (formik.touched.phaseId && formik.errors.phaseId) ||
                  (!formik.values.projectId ? "Select a project first" : "")
                }
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">
                  <em>No phase</em>
                </MenuItem>
                {phaseOptions.map((phase) => (
                  <MenuItem key={phase.id} value={String(phase.id)}>
                    {phase.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2.5}>
              <Typography variant="subtitle2" component="div" gutterBottom fontWeight={600}>
                Category
              </Typography>
              <TextField
                select
                fullWidth
                id="category"
                name="category"
                value={formik.values.category}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.category && Boolean(formik.errors.category)}
                helperText={formik.touched.category && formik.errors.category}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">
                  <em>Select a category</em>
                </MenuItem>
                {EXPENSE_CATEGORIES.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={6} lg={1.5}>
              <Typography variant="subtitle2" component="div" gutterBottom fontWeight={600}>
                Amount
              </Typography>
              <TextField
                fullWidth
                id="amount"
                name="amount"
                type="number"
                inputProps={{ step: "0.01", min: "0", inputMode: "decimal" }}
                placeholder="0.00"
                value={formik.values.amount}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.amount && Boolean(formik.errors.amount)}
                helperText={formik.touched.amount && formik.errors.amount}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6} lg={2.5}>
              <Typography variant="subtitle2" component="div" gutterBottom fontWeight={600}>
                Date
              </Typography>
              <TextField
                fullWidth
                id="date"
                name="date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formik.values.date}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.date && Boolean(formik.errors.date)}
                helperText={formik.touched.date && formik.errors.date}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" component="div" gutterBottom fontWeight={600}>
                Notes
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                id="notes"
                name="notes"
                placeholder="Optional details about this expense"
                value={formik.values.notes}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" component="div" gutterBottom fontWeight={600}>
                Receipt
              </Typography>

              {/* capture="environment" opens the rear camera on phones; the second
                  input has no capture attribute so it opens the gallery/file picker. */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleReceiptChange}
                style={{ display: "none" }}
              />
              <input
                ref={galleryInputRef}
                type="file"
                accept="image/*,application/pdf"
                onChange={handleReceiptChange}
                style={{ display: "none" }}
              />

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                sx={{ mb: 2, maxWidth: 520 }}
              >
                <Button
                  variant="outlined"
                  startIcon={<PhotoCamera />}
                  onClick={() => cameraInputRef.current?.click()}
                  fullWidth
                  sx={{ color: "primary.main", borderColor: "primary.main", py: 1.25 }}
                >
                  Take Photo
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<PhotoLibrary />}
                  onClick={() => galleryInputRef.current?.click()}
                  fullWidth
                  sx={{ color: "primary.main", borderColor: "primary.main", py: 1.25 }}
                >
                  Choose File or PDF
                </Button>
              </Stack>

              {previewSrc && (
                <Box
                  sx={{
                    position: "relative",
                    display: "inline-block",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    padding: 1,
                  }}
                >
                  {previewIsPdf ? (
                    <Box
                      component="a"
                      href={previewSrc}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        minWidth: 240,
                        pr: 4,
                        py: 1,
                        textDecoration: "none",
                        color: "text.primary",
                      }}
                    >
                      <PictureAsPdfIcon sx={{ fontSize: 36, color: "error.main" }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {receiptFile?.name || "Receipt.pdf"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          PDF · opens in a new tab
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      component="img"
                      src={previewSrc}
                      alt="Receipt preview"
                      sx={{
                        display: "block",
                        maxWidth: "100%",
                        maxHeight: 260,
                        borderRadius: 1,
                      }}
                    />
                  )}
                  <IconButton
                    aria-label="Remove receipt"
                    onClick={clearReceipt}
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      bgcolor: "rgba(255,255,255,0.9)",
                      "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                    }}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Grid>

            <Grid item xs={12}>
              <Stack
                direction={{ xs: "column-reverse", sm: "row" }}
                spacing={2}
                justifyContent="flex-end"
              >
                <Button
                  variant="outlined"
                  onClick={() => navigate(`${basePath}/expenses`)}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button type="submit" variant="contained" color="primary" disabled={loading}>
                  {loading ? "Saving..." : isEditing ? "Update Expense" : "Save Expense"}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
}

export default AddExpense;
